import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import UPMultipleSelectWithCheckbox from 'components/multiple-select-with-checkbox/UPMultipleSelectWithCheckbox';
import UPSwitch from 'components/switch';
import { AUTH_MODE_LABEL_KEYS, pickUnitFields } from 'modules/management/time-registration-policy/helpers';
import { SwitchLabel, SwitchRow } from 'modules/management/time-registration-policy/styles';
import { UnitDefinition } from 'modules/management/time-registration-policy/units';
import { useTranslation } from 'react-i18next';
import {
  TIME_REGISTRATION_POLICY__FIELD_AUTH_MODE,
  TIME_REGISTRATION_SETTINGS__ADD_LOCATION_OVERRIDE,
  TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_EMPTY,
  TIME_REGISTRATION_SETTINGS__OVERRIDE_LOCATIONS,
  TIME_REGISTRATION_SETTINGS__OVERRIDE_LOCATIONS_PLACEHOLDER,
  TIME_REGISTRATION_SETTINGS__REMOVE_LOCATION_OVERRIDE,
} from 'translations/constants';
import { KioskAuthenticationMode } from 'types/company/kiosk/IKioskSettings';
import {
  CompanyPolicyLocation,
  LocationOverride,
  OverrideErrors,
} from 'types/company/time-registration/ICompanyTimeRegistrationPolicy';
import { UnitValue } from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { OverrideActions, OverrideCard, OverrideList, StateHelp } from './styles';

interface LocationOverridesEditorProps {
  def: UnitDefinition;
  overrides: LocationOverride[];
  locations: CompanyPolicyLocation[];
  errors: OverrideErrors[];
  companyDefault: UnitValue;
  disabled: boolean;
  onChange: (next: LocationOverride[]) => void;
}

const AUTH_MODES = [
  KioskAuthenticationMode.OpenList,
  KioskAuthenticationMode.Pin,
  KioskAuthenticationMode.PinPlusPersonalDeviceApproval,
];

const LocationOverridesEditor = ({
  def,
  overrides,
  locations,
  errors,
  companyDefault,
  disabled,
  onChange,
}: LocationOverridesEditorProps): JSX.Element => {
  const [t] = useTranslation();

  const locationName = (id: number): string => locations.find(location => location.id === id)?.name ?? String(id);
  const update = (index: number, patch: Partial<LocationOverride>) =>
    onChange(overrides.map((override, i) => (i === index ? { ...override, ...patch } : override)));
  const remove = (index: number) => onChange(overrides.filter((_, i) => i !== index));
  const add = () =>
    onChange([
      ...overrides,
      {
        value: def.kind === 'authMode' ? { authenticationMode: null } : pickUnitFields(def, companyDefault),
        locationIds: [],
      },
    ]);
  const takenElsewhere = (index: number): number[] =>
    overrides.flatMap((override, i) => (i === index ? [] : override.locationIds));

  const renderValue = (override: LocationOverride, index: number) =>
    def.kind === 'authMode' ? (
      <UPDropdown
        label={t(TIME_REGISTRATION_POLICY__FIELD_AUTH_MODE)}
        items={AUTH_MODES.map(mode => ({ label: t(AUTH_MODE_LABEL_KEYS[mode]), value: String(mode) }))}
        value={override.value.authenticationMode ? String(override.value.authenticationMode) : ''}
        onChange={(event: { target: { value: string } }) =>
          update(index, { value: { authenticationMode: event.target.value ? Number(event.target.value) : null } })
        }
        error={!!errors[index]?.authenticationMode}
        helperText={errors[index]?.authenticationMode ? t(errors[index].authenticationMode.key) : ''}
        disabled={disabled}
        testId={`tr-company-override-${index}-mode`}
      />
    ) : (
      <SwitchRow>
        <SwitchLabel>{t(def.nameKey)}</SwitchLabel>
        <UPSwitch
          checked={!!override.value.enabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update(index, { value: { enabled: event.target.checked } })
          }
          disabled={disabled}
          testId={`tr-company-override-${index}-toggle`}
        />
      </SwitchRow>
    );

  return (
    <OverrideList data-testid="tr-company-overrides">
      {!overrides.length && (
        <StateHelp data-testid="tr-company-overrides-empty">
          {t(TIME_REGISTRATION_SETTINGS__LOCATION_OVERRIDES_EMPTY)}
        </StateHelp>
      )}
      {overrides.map((override, index) => {
        const taken = takenElsewhere(index);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <OverrideCard key={index} data-testid={`tr-company-override-${index}`}>
            {renderValue(override, index)}
            <UPMultipleSelectWithCheckbox
              label={t(TIME_REGISTRATION_SETTINGS__OVERRIDE_LOCATIONS)}
              required
              placeholder={t(TIME_REGISTRATION_SETTINGS__OVERRIDE_LOCATIONS_PLACEHOLDER)}
              items={locations.map(location => ({
                label: location.name,
                value: String(location.id),
                isDisabled: taken.includes(location.id),
              }))}
              selectedItems={override.locationIds.map(String)}
              onChange={(event: { target: { value: string[] } }) =>
                update(index, { locationIds: event.target.value.map(Number) })
              }
              renderValue={value => (value as string[]).map(id => locationName(Number(id))).join(', ')}
              error={!!errors[index]?.locationIds}
              helperText={errors[index]?.locationIds ? t(errors[index].locationIds.key) : ''}
              disabled={disabled}
              testId={`tr-company-override-${index}-locations`}
            />
            {!disabled && (
              <OverrideActions>
                <UPButton
                  text={t(TIME_REGISTRATION_SETTINGS__REMOVE_LOCATION_OVERRIDE)}
                  outlined
                  onClick={() => remove(index)}
                  testId={`tr-company-override-${index}-remove`}
                />
              </OverrideActions>
            )}
          </OverrideCard>
        );
      })}
      {!disabled && (
        <OverrideActions>
          <UPButton
            text={t(TIME_REGISTRATION_SETTINGS__ADD_LOCATION_OVERRIDE)}
            outlined
            onClick={add}
            testId="tr-company-override-add"
          />
        </OverrideActions>
      )}
    </OverrideList>
  );
};

export default LocationOverridesEditor;
